import { useState } from "react";
import api from "@/lib/api";
import { X, CheckCircle2 } from "lucide-react";

// CC-225: record CSR commission paid outside the app (payroll editors). Single or bulk by job #.
export const MarkPaidModal = ({ slug, initialJobs = "", onClose, onDone }) => {
  const [jobs, setJobs] = useState(initialJobs);
  const [paidOn, setPaidOn] = useState("");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [res, setRes] = useState(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/csr-sales/mark-paid?tenant_slug=${slug}`,
        { job_numbers: jobs, paid_on: paidOn, reference: reference.trim() });
      setRes(data); onDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail; setErr(typeof d === "string" ? d : JSON.stringify(d || e.message));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" data-testid="csr-mark-paid-modal">
      <div className="bg-white rounded-xl w-full max-w-[560px] p-5 space-y-3">
        <div className="flex items-center">
          <div className="font-semibold text-[15px]">Mark CSR commission paid (outside the app)</div>
          <button className="ml-auto" onClick={onClose} data-testid="csr-mark-paid-close"><X size={16} /></button>
        </div>
        <p className="text-[12px] text-[color:var(--cc-ink-muted)]">
          Marked sales are left out of every later payroll compute and the CSR Backpay report.
        </p>
        <label className="block text-[12px] font-semibold">Job numbers (one or many — commas, spaces or new lines)
          <textarea className="mt-1 w-full border border-[#d8dce3] rounded px-2 py-1.5 text-[12.5px] font-mono-data h-24"
                    value={jobs} onChange={(e) => setJobs(e.target.value)} data-testid="csr-mark-paid-jobs" />
        </label>
        <div className="flex gap-3">
          <label className="text-[12px] font-semibold">Paid date
            <input type="date" className="block mt-1 border border-[#d8dce3] rounded px-2 py-1 text-[12.5px]"
                   value={paidOn} onChange={(e) => setPaidOn(e.target.value)} data-testid="csr-mark-paid-date" />
          </label>
          <label className="text-[12px] font-semibold flex-1">Reference
            <input className="block mt-1 w-full border border-[#d8dce3] rounded px-2 py-1 text-[12.5px]"
                   placeholder="Paid 10/13 per Kat's 10/12 list, approved by Nathanael"
                   value={reference} onChange={(e) => setReference(e.target.value)} data-testid="csr-mark-paid-reference" />
          </label>
        </div>
        {err && <div className="text-[12px] text-[#b42318]" data-testid="csr-mark-paid-err">{err}</div>}
        {res && (
          <div className="text-[12px] space-y-1 bg-[#f6faf7] border border-[#cfe6d6] rounded p-2" data-testid="csr-mark-paid-result">
            <div className="flex items-center gap-1 font-semibold text-[#14532d]"><CheckCircle2 size={13} /> Marked paid: {res.marked.length}</div>
            {res.already_marked.length > 0 && <div>Already marked (unchanged): {res.already_marked.map((r) => r.job_number).join(", ")}</div>}
            {res.no_csr_sale.length > 0 && <div data-testid="csr-mark-paid-no-sale">No CSR sale (unchanged): {res.no_csr_sale.join(", ")}</div>}
            {res.already_paid_in_run.length > 0 && (
              <div className="text-[#b42318] font-semibold" data-testid="csr-mark-paid-double">
                Already paid in an approved run — check for a double payment: {res.already_paid_in_run.map((r) => `${r.job_number} (run of ${r.already_paid_in_run.pay_week_monday})`).join(", ")}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button className="px-3 py-1.5 text-[12.5px]" onClick={onClose}>Close</button>
          <button className="px-3 py-1.5 rounded bg-[#0e1420] text-white text-[12.5px] font-semibold disabled:opacity-50"
                  disabled={busy || !jobs.trim() || !paidOn || reference.trim().length < 5} onClick={submit}
                  data-testid="csr-mark-paid-submit">{busy ? "Saving…" : "Mark paid"}</button>
        </div>
      </div>
    </div>
  );
};

export const undoPaid = async (slug, jobNumber) => {
  const reason = window.prompt(`Undo "paid outside" for job ${jobNumber}? Reason (10+ characters):`);
  if (!reason) return false;
  await api.post(`/csr-sales/undo-paid?tenant_slug=${slug}`, { job_number: jobNumber, reason });
  return true;
};

export const PaidBadge = ({ r }) => (r.paid_outside ? (
  <span data-testid={`csr-sales-paid-badge-${r.job_number}`}
        title={`Paid ${r.paid_outside.paid_on} · ${r.paid_outside.reference} · recorded by ${r.paid_outside.recorded_by}`}
        className="inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded bg-[#e6f4ea] text-[#14532d] border border-[#b7dfc3]">
    Paid {r.paid_outside.paid_on}
  </span>
) : null);
